import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ReloadIcon, Cross2Icon } from '@radix-ui/react-icons'
import { CptCard } from '../LayerPanel/ui'
import {
  CptUploadJob,
  INTERRUPTED_ERROR,
  jobMatchesLayer,
  useCptUploadStore,
} from './uploadQueue'

const statusLabel = (job: CptUploadJob) => {
  if (job.status === 'queued') return '排队中'
  if (job.status === 'padding') return `制作 2048 ${job.progress}%`
  if (job.status === 'compressing') return `压缩中 ${job.progress}%`
  if (job.status === 'processing') return '处理中'
  if (job.status === 'error') {
    return job.error === INTERRUPTED_ERROR ? '中断' : '失败'
  }
  return `${job.progress}%`
}

const UploadingCptCard = ({ job }: { job: CptUploadJob }) => {
  const retryJob = useCptUploadStore((s) => s.retryJob)
  const removeJob = useCptUploadStore((s) => s.removeJob)
  const failed = job.status === 'error'

  return (
    <CptCard
      className={cn(failed && 'border-destructive')}
      children2={
        failed ? (
          <div className="mt-1 flex w-full items-center justify-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2"
              onClick={() => retryJob(job.id)}
              title={job.error || '重试'}
            >
              <ReloadIcon className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={() => removeJob(job.id)}
            >
              <Cross2Icon className="h-3 w-3" />
            </Button>
          </div>
        ) : null
      }
    >
      <div className="relative w-full aspect-square overflow-hidden rounded-[10px]">
        {job.preview ? (
          <img
            src={job.preview}
            alt={job.fileName}
            className="h-full w-full object-contain"
          />
        ) : null}
        <div className="absolute inset-x-0 bottom-0 bg-black/55 px-1.5 py-1">
          <div className="h-1 overflow-hidden rounded-full bg-white/25">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-200',
                failed ? 'bg-destructive' : 'bg-primary',
              )}
              style={{
                width: `${failed ? 100 : Math.max(job.progress, 6)}%`,
              }}
            />
          </div>
          <div className="mt-0.5 truncate text-center text-[10px] leading-tight text-white">
            {statusLabel(job)}
          </div>
          {job.fileName ? (
            <div
              className="truncate text-center text-[10px] leading-tight text-white/80"
              title={job.fileName}
            >
              {job.fileName}
            </div>
          ) : null}
        </div>
      </div>
    </CptCard>
  )
}

export const UploadingCptCards = ({
  tid,
  lid,
  dlc,
}: {
  tid?: string
  lid?: string
  dlc?: string | null
}) => {
  const jobs = useCptUploadStore((s) =>
    s.jobs.filter((job) => {
      if (job.tid !== tid) return false
      if (!jobMatchesLayer(job, lid)) return false
      if (dlc == null || dlc === '') return !job.dlc
      return job.dlc === dlc
    }),
  )
  if (jobs.length === 0) return null
  return (
    <>
      {jobs.map((job) => (
        <UploadingCptCard key={job.id} job={job} />
      ))}
    </>
  )
}

export default UploadingCptCard
